import { pollInterval } from '../realtime/live-updates';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  GenerateQuotationInput,
  ListQuotationsQuery,
  QuotationCounts,
  QuotationFilters,
  QuotationGenerationJob,
  QuotationPreviewResult,
  QuotationResource,
  SendQuotationInput,
  UpdateQuotationItemsInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['quotations'] as const;
const JOBS_KEY = ['quotations', 'generation-jobs'] as const;
/** While any job is still PENDING/PROCESSING, poll the ongoing-quotations panel this often. */
const JOB_POLL_MS = 4_000;

export interface QuotationListPage {
  items: QuotationResource[];
  meta: CollectionMeta;
}

function filterQuery(q: QuotationFilters): Record<string, string | number | undefined> {
  return {
    status: q.status,
    contactId: q.contactId,
    customer: q.customer,
    reference: q.reference,
    totalMin: q.totalMin,
    totalMax: q.totalMax,
    createdAfter: q.createdAfter,
    createdBefore: q.createdBefore,
  };
}

function toQuery(q: ListQuotationsQuery): Record<string, string | number | undefined> {
  return {
    ...filterQuery(q),
    page: q.page,
    pageSize: q.pageSize,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listQuotations(q: ListQuotationsQuery = {}): Promise<QuotationListPage> {
  const res = await apiClient.getCollection<QuotationResource>('/quotations', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getQuotation = (id: string) => apiClient.get<QuotationResource>(`/quotations/${id}`);
export const downloadQuotationPdf = (id: string) => apiClient.getBlob(`/quotations/${id}/pdf`);

export function useQuotations(query: ListQuotationsQuery = {}) {
  return useQuery<QuotationListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listQuotations(query),
    placeholderData: keepPreviousData,
  });
}

/** All / per-status counts for the grid tabs, under the same filters (status ignored). */
export function useQuotationCounts(query: QuotationFilters = {}) {
  const { status: _status, ...filters } = query;
  return useQuery<QuotationCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () =>
      apiClient.get<QuotationCounts>('/quotations/counts', { query: filterQuery(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useQuotation(id: string | undefined) {
  return useQuery<QuotationResource, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getQuotation(id!),
    enabled: Boolean(id),
  });
}

/**
 * Downloads a quotation's PDF and hands it to the browser's own save-file
 * flow (a temporary object URL + a synthetic `<a download>` click - the
 * same approach `useDownloadMessageMedia` uses, since a plain `<a href>`
 * can't carry the Authorization header this endpoint needs).
 */
export function useDownloadQuotationPdf() {
  return useMutation<void, ApiClientError, string>({
    mutationFn: async (quotationId) => {
      const { blob, fileName } = await downloadQuotationPdf(quotationId);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName ?? 'quotation.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  });
}

// --- Staff-generated (manual/photo-upload) quotations - NewQuotationPage ---

export interface PreviewManualQuotationTextInput {
  contactId: string;
  itemsText: string;
}

/**
 * The file-upload branch of `POST /quotations/generate/preview` - AI
 * extraction over one or more photos/PDFs is too slow to hold the browser
 * request open for, so the backend now runs it in the background and
 * returns a job immediately (`202`) instead of the preview directly. Poll
 * `useQuotationGenerationJob`/`useQuotationGenerationJobs` for progress and
 * the eventual result.
 */
export interface CreateQuotationGenerationJobInput {
  contactId: string;
  files: File[];
}

export const createQuotationGenerationJob = ({
  contactId,
  files,
}: CreateQuotationGenerationJobInput) => {
  const form = new FormData();
  form.append('contactId', contactId);
  for (const file of files) form.append('file', file, file.name);
  return apiClient.post<QuotationGenerationJob>('/quotations/generate/preview', form);
};

export const previewManualQuotationFromText = ({
  contactId,
  itemsText,
}: PreviewManualQuotationTextInput) =>
  apiClient.post<QuotationPreviewResult>('/quotations/generate/preview', {
    contactId,
    itemsText,
  });

export const generateQuotation = (input: GenerateQuotationInput) =>
  apiClient.post<QuotationResource>('/quotations/generate', input);

export const updateQuotationItems = (quotationId: string, input: UpdateQuotationItemsInput) =>
  apiClient.put<QuotationResource>(`/quotations/${quotationId}/items`, input);

export function useUpdateQuotationItems(quotationId: string) {
  const qc = useQueryClient();
  return useMutation<QuotationResource, ApiClientError, UpdateQuotationItemsInput>({
    mutationFn: (input) => updateQuotationItems(quotationId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export const sendQuotation = (quotationId: string, input: SendQuotationInput) =>
  apiClient.post<QuotationResource>(`/quotations/${quotationId}/send`, input);

export function useCreateQuotationGenerationJob() {
  const qc = useQueryClient();
  return useMutation<QuotationGenerationJob, ApiClientError, CreateQuotationGenerationJobInput>({
    mutationFn: createQuotationGenerationJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: JOBS_KEY }),
  });
}

function isJobOngoing(job: QuotationGenerationJob): boolean {
  return job.status === 'PENDING' || job.status === 'PROCESSING';
}

export const listQuotationGenerationJobs = async (): Promise<QuotationGenerationJob[]> =>
  (
    await apiClient.getCollection<QuotationGenerationJob>('/quotations/generation-jobs', {
      query: { pageSize: 20 },
    })
  ).data;

/** The "ongoing quotations" progress panel - polls only while at least one job is still running. */
export function useQuotationGenerationJobs() {
  return useQuery<QuotationGenerationJob[], ApiClientError>({
    queryKey: JOBS_KEY,
    queryFn: listQuotationGenerationJobs,
    refetchInterval: (query) => {
      const jobs = query.state.data;
      return jobs && jobs.some(isJobOngoing) ? pollInterval(JOB_POLL_MS)() : false;
    },
  });
}

export const getQuotationGenerationJob = (jobId: string) =>
  apiClient.get<QuotationGenerationJob>(`/quotations/generation-jobs/${jobId}`);

/** A single job's own detail view (e.g. opened from the notification deep-link) - same polling-until-terminal behavior as the list. */
export function useQuotationGenerationJob(jobId: string | undefined) {
  return useQuery<QuotationGenerationJob, ApiClientError>({
    queryKey: [...JOBS_KEY, jobId],
    queryFn: () => getQuotationGenerationJob(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      query.state.data && isJobOngoing(query.state.data) ? pollInterval(JOB_POLL_MS)() : false,
  });
}

export function usePreviewManualQuotationFromText() {
  return useMutation<QuotationPreviewResult, ApiClientError, PreviewManualQuotationTextInput>({
    mutationFn: previewManualQuotationFromText,
  });
}

export function useGenerateQuotation() {
  const qc = useQueryClient();
  return useMutation<QuotationResource, ApiClientError, GenerateQuotationInput>({
    mutationFn: generateQuotation,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSendQuotation() {
  const qc = useQueryClient();
  return useMutation<
    QuotationResource,
    ApiClientError,
    { quotationId: string; input: SendQuotationInput }
  >({
    mutationFn: ({ quotationId, input }) => sendQuotation(quotationId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
